import { fetchApi } from './client';

// The two calls the checkout's discount code field makes: whether the store has a code to offer at all, and what
// a typed code takes off this cart. The order itself is priced again by the server, so these only preview it.

/** Why the server refused a code, as it names it. */
export type DiscountRefusal = 'NOT_FOUND' | 'NOT_STARTED' | 'EXPIRED' | 'USAGE_LIMIT_REACHED' | 'MINIMUM_AMOUNT_NOT_MET'
  | 'MINIMUM_QUANTITY_NOT_MET' | 'CART_DETAILS_REQUIRED' | 'PRODUCTS_NOT_ELIGIBLE' | 'ALREADY_REDEEMED';

/** A code's answer for a cart: what it takes off, or why it does not apply. */
export type CodeCheck =
  | { isValid: true; code: string; title: string; discountAmount: number; shippingDiscount: number; totalDiscount: number }
  | { isValid: false; reason: DiscountRefusal; error: string };

/** The cart a code is checked against: its priced lines, their total, and the delivery fee. */
export interface CodeCart {
  lineItems: Array<{ productId: string; quantity: number; lineTotal: number }>;
  subtotal: number;
  shippingFee: number;
}

/** What the store runs right now: codes a shopper can type, and automatic discounts that apply themselves. */
export type DiscountOffers = { codes: boolean; automatic: boolean };

/** The store's best automatic discount for a cart: its title and what it takes off. */
export type AutomaticSaving = { title: string; totalDiscount: number };

// Asks whether the store has a live code to type and a live automatic discount, so the checkout offers only what can work.
export async function discountOffers(): Promise<DiscountOffers> {
  const data = await fetchApi<{ available: boolean; automatic?: boolean }>('/checkouts/discount-codes');
  return { codes: data.available, automatic: data.automatic === true };
}

// Asks for the store's best automatic discount on this cart, or null when none applies.
export async function automaticDiscount(cart: CodeCart): Promise<AutomaticSaving | null> {
  return (await fetchApi<{ applied: AutomaticSaving | null }>('/checkouts/automatic-discount', { method: 'POST', body: JSON.stringify(cart) })).applied;
}

// Checks a typed code against the cart and answers what it takes off, or why not.
export async function checkDiscountCode(code: string, cart: CodeCart): Promise<CodeCheck> {
  return await fetchApi<CodeCheck>('/offers/discounts/validate', { method: 'POST', body: JSON.stringify({ code, ...cart }) });
}
